import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { CUST, CUST_ANIM } from '../../../../theme/customizer-tokens';

/**
 * One logo slot — the SAME card whether it's still generating (dot field +
 * typewriter caption) or has a logo revealing inside it. The whole card is the
 * render plate (a solid colour so the luminance-driven light⇄dark swap cross-fades
 * over 400ms); selected adds the orange ring. No inset frame → no thick border.
 */
export const Card = styled.button<{ $selected: boolean; $compact: boolean; $frame: 'light' | 'dark' }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '0' : '16px')};
  width: 100%;
  /* Desktop: fill the grid cell (the grid fills the viewport) so the card is a
     fixed size and never reflows. Mobile: natural height in the scrolling list. */
  height: ${({ $compact }) => ($compact ? 'auto' : '100%')};
  min-height: 0;
  padding: ${({ $compact }) => ($compact ? '12px' : '24px')};
  border-radius: 22px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  border: 1px solid ${({ $selected }) => ($selected ? CUST.accent : 'transparent')};
  background-color: ${({ $frame }) => ($frame === 'light' ? CUST.logoFrameLight : CUST.genCardDark)};
  transition:
    background-color 400ms ease,
    border-color ${CUST_ANIM.INTERACT_MS}ms ease,
    filter ${CUST_ANIM.INTERACT_MS}ms ease;

  &:hover {
    filter: brightness(1.06);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      box-shadow: 0 0 0 1px ${CUST.accent};
    `}
`;

/** Sizes + centres the logo asset. Desktop: flexes to fill the card above the
 *  (fixed) caption — so the slot is a fixed size and the logo scales to fit.
 *  Mobile: fixed height in the scrolling list. No overflow clip, so the blurred
 *  logo feathers (the rounded card already clips the overall shape). */
export const ImageFrame = styled.div<{ $compact: boolean }>`
  position: relative;
  width: 100%;
  ${({ $compact }) =>
    $compact
      ? css`
          height: 76px;
          padding: 12px;
        `
      : css`
          flex: 1 1 auto;
          min-height: 0;
          padding: 16px;
        `}
`;

/** Fills the padded image area; both logo copies stack (overlap) inside it. */
export const LogoStack = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const logoCopy = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

/**
 * The reveal cross-fades a pre-blurred copy OUT and the sharp copy IN (rather than
 * animating the blur filter), so the deblur is GPU-smooth and crisp. Both copies
 * share the settle scale; this copy's blur is CONSTANT (never animated).
 *
 * First 1s (the hold): ONLY this copy's opacity animates (0→1) beneath the grid.
 * Then $deblur flips → it fades out (760ms) while the sharp copy fades in.
 */
export const LogoBlurred = styled.img<{ $shown: boolean; $deblur: boolean }>`
  ${logoCopy};
  filter: blur(42px);
  transform: scale(${({ $deblur }) => ($deblur ? 1 : 1.06)});
  opacity: ${({ $shown, $deblur }) => ($shown && !$deblur ? 1 : 0)};
  transition:
    opacity ${({ $deblur }) => ($deblur ? '760ms' : '1000ms')} ease,
    transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
`;

/** The crisp copy — hidden during the hold, then cross-fades in (760ms) as the
 *  blurred copy fades out. */
export const LogoSharp = styled.img<{ $deblur: boolean }>`
  ${logoCopy};
  transform: scale(${({ $deblur }) => ($deblur ? 1 : 1.06)});
  opacity: ${({ $deblur }) => ($deblur ? 1 : 0)};
  transition:
    opacity 760ms ease,
    transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
`;

/** Fixed 2-line caption slot (always reserved, so the card height is stable). The
 *  description fades in only AFTER the reveal completes ($shown). The ink tracks the
 *  card's luminance-driven plate: near-black on the light plate (dark logos), the
 *  muted grey on the dark plate (light logos), so the blurb always has contrast. */
export const Caption = styled(Text)<{ $shown: boolean; $frame: 'light' | 'dark' }>`
  flex: 0 0 auto;
  height: 40px;
  font-size: 14px;
  line-height: 20px;
  color: ${({ $frame }) => ($frame === 'light' ? CUST.logoCaptionInk : CUST.logoCaption)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity 450ms ease;
`;

/** The "generating" layer — the dot field + typewriter caption — covering the full
 *  card. Transparent (the card plate is its backdrop) so the logo shows fading in
 *  beneath the dots, and the dots reach border-to-border. Fades out as the logo
 *  sharpens, then unmounts (stops the canvas). */
export const GenOverlay = styled.div<{ $fading: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${({ $fading }) => ($fading ? 0 : 1)};
  transition: opacity 720ms ease;
`;

/** The self-typing status caption, top-left, very subtle, with a solid 5px halo
 *  (a 10px stroke behind the fill) so no dots touch the text. Ink + halo track the
 *  card's luminance-driven plate so that, once a card swaps to its light plate while
 *  the overlay is still fading, the label reconciles (a near-black ink over a halo of
 *  the light plate colour) instead of reading as a dark smudge. */
export const Label = styled(Text)<{ $visible: boolean; $frame: 'light' | 'dark' }>`
  position: absolute;
  top: 13px;
  left: 15px;
  right: 15px;
  z-index: 1;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.3;
  color: ${({ $frame }) => ($frame === 'light' ? CUST.genLabelInkLight : CUST.genLabelInk)};
  -webkit-text-stroke: 10px ${({ $frame }) => ($frame === 'light' ? CUST.genLabelHaloLight : CUST.genLabelHalo)};
  paint-order: stroke;
  text-shadow:
    0 0 3px ${({ $frame }) => ($frame === 'light' ? CUST.genLabelHaloLight : CUST.genLabelHalo)},
    0 0 6px ${({ $frame }) => ($frame === 'light' ? CUST.genLabelHaloLight : CUST.genLabelHalo)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 320ms ease;
`;
